// src/components/AssetTab.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Swal from 'sweetalert2';
import TomSelect from 'tom-select';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { Building, RotateCw, QrCode, Search, Store } from 'lucide-react';
import {
  getAllAssets,
  searchAssets,
  savePengajuan,
  getAllPengajuan,
  saveAllAssets,
  getLastSyncTime,
  updateAssetLocation,
  updatePengajuanStatus,
} from '../db/assetDB';

const API_BASE = 'https://jne-cilacap.com/slip/public/api';
const ALLOWED_LOCATION_PREFIXES = [
  'depan',
  'ruang',
  'kp',
  'agen',
  'counter',
  'cash',
  'outbound',
  'inbound',
  'delivery',
  'undel',
  'pickup',
];

const filterAllowedLocations = (items = []) => {
  const filtered = items.filter((item) => {
    const name = String(item?.nama || item?.name || '').toLowerCase().trim();
    return ALLOWED_LOCATION_PREFIXES.some((prefix) => name.startsWith(prefix));
  });

  return filtered.length ? filtered : items;
};

const JENIS_PENANGANAN = [
  { id: 'service', label: '🔧 Service / Perbaikan', desc: 'Kerusakan fisik, tidak berfungsi' },
  { id: 'isi_ulang', label: '🖨️ Isi Ulang', desc: 'Tinta, toner, gas, dll' },
  { id: 'kalibrasi', label: '📐 Kalibrasi', desc: 'Penyesuaian akurasi alat' },
  { id: 'penggantian', label: '🔄 Penggantian Komponen', desc: 'Spare part, aksesori' },
  { id: 'lainnya', label: '📝 Lainnya', desc: 'Keluhan atau kebutuhan lain' },
];

// ── Config ikon per jenis penanganan ─────────────────────────────────────────
const JENIS_CONFIG = {
  'Service': { icon: 'ti-tool', bg: '#EAF3DE', color: '#3B6D11' },
  'Pembelian Sparepart': { icon: 'ti-puzzle', bg: '#E6F1FB', color: '#185FA5' },
  'Pajak': { icon: 'ti-receipt-tax', bg: '#FAEEDA', color: '#854F0B' },
  'KIR': { icon: 'ti-clipboard-check', bg: '#FCEBEB', color: '#A32D2D' },
  'Tagihan': { icon: 'ti-bolt', bg: '#EEEDFE', color: '#534AB7' },
  'Isi Ulang': { icon: 'ti-reload', bg: '#E1F5EE', color: '#0F6E56' },
  'Tera Ulang': { icon: 'ti-rotate', bg: '#FAEEDA', color: '#633806' },
  'Ganti Baru': { icon: 'ti-package', bg: '#FBEAF0', color: '#993556' },
  'pengadaan': { icon: 'ti-shopping-cart', bg: '#E6F1FB', color: '#0C447C' },
  'Lainya': { icon: 'ti-dots-circle-horizontal', bg: '#F1EFE8', color: '#5F5E5A' },
};

// ── Helpers ──────────────────────────────────────────────────────────────────
const getKondisiStyle = (kondisi) => {
  if (kondisi === 'Baik') return { bg: '#dcfce7', color: '#166534' };
  if (kondisi === 'Rusak Ringan') return { bg: '#fef9c3', color: '#854d0e' };
  return { bg: '#fee2e2', color: '#991b1b' };
};

const getAssetIcon = (kategori = '') => {
  const k = kategori.toLowerCase();
  if (k.includes('it') || k.includes('komputer')) return '💻';
  if (k.includes('kendaraan')) return '🚗';
  if (k.includes('furnitur')) return '🪑';
  if (k.includes('kebersihan')) return '🧹';
  return '📦';
};

// ── Badge status ──────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    Pending: { bg: '#7e7d73ff', color: '#f4f2efff', label: '⏳ Ngantri' },
    Diproses: { bg: '#dbeafe', color: '#1e40af', label: '⚙️ Diproses' },
    Selesai: { bg: '#dcfce7', color: '#166534', label: '✅ Selesai' },
    Ditolak: { bg: '#fee2e2', color: '#991b1b', label: '❌ Ditolak' },
  };
  const s = map[status] || map.Pending;
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: '3px 8px',
      borderRadius: 8, backgroundColor: s.bg, color: s.color,
    }}>
      {s.label}
    </span>
  );
};

// ── TomSelect wrapper ─────────────────────────────────────────────────────────
const TomSelectField = ({ selectRef, options, onChange, placeholder }) => {
  const instanceRef = useRef(null);

  useEffect(() => {
    if (!selectRef.current || options.length === 0) return;

    if (instanceRef.current) {
      const cur = instanceRef.current.getValue();
      instanceRef.current.clearOptions();
      instanceRef.current.addOptions(options);
      instanceRef.current.refreshOptions(false);
      if (cur) instanceRef.current.setValue(cur, true);
      return;
    }

    instanceRef.current = new TomSelect(selectRef.current, {
      valueField: 'id',
      labelField: 'nama',
      searchField: 'nama',
      placeholder,
      options,
      render: {
        option: (d, esc) => `
          <div style="padding:8px 12px;border-bottom:1px solid #f1f5f9">
            <div style="font-weight:600;color:#1e293b;font-size:13px">${esc(d.nama)}</div>
          </div>`,
        item: (d, esc) => `<div style="font-weight:500;font-size:13px">${esc(d.nama)}</div>`,
      },
      onChange: (v) => onChange(v),
    });

    return () => {
      instanceRef.current?.destroy();
      instanceRef.current = null;
    };
  }, [options]);

  return null;
};

// ── Tombol aksi (card style) ──────────────────────────────────────────────────
const ActionButton = ({ onClick, bgColor, iconBg, icon, title, desc, titleColor, descColor, arrowColor, style }) => (
  <button
    onClick={onClick}
    style={{
      width: '100%', display: 'flex', alignItems: 'center', gap: 14,
      backgroundColor: bgColor, border: `1px solid ${bgColor}`,
      borderRadius: 16, padding: '14px 16px', marginBottom: 10,
      cursor: 'pointer', transition: 'opacity 0.15s, transform 0.1s',
      ...style,
    }}
    onMouseDown={e => e.currentTarget.style.transform = 'scale(0.98)'}
    onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
  >
    <div style={{
      width: 44, height: 44, borderRadius: 12, flexShrink: 0,
      backgroundColor: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 20,
    }}>
      {icon}
    </div>
    <div style={{ flex: 1, textAlign: 'left' }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: titleColor, marginBottom: 2 }}>{title}</div>
      <div style={{ fontSize: 12, color: descColor }}>{desc}</div>
    </div>
    <span style={{ fontSize: 18, color: arrowColor, opacity: 0.7 }}>›</span>
  </button>
);

// ── Asset History Modal ────────────────────────────────────────────────────────
const AssetHistoryModal = ({ asset, onClose, isOpen }) => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandPenanganan, setExpandPenanganan] = useState(false);
  const [expandPerpindahan, setExpandPerpindahan] = useState(false);

  const ITEMS_LIMIT = 5;

  useEffect(() => {
    if (!isOpen || !asset) return;

    const fetchHistory = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/assets/${asset.id}/history`);
        const json = await res.json();
        if (json.status === 'success') {
          setHistory(json.data);
        } else {
          setError(json.message || 'Gagal mengambil history');
        }
      } catch (e) {
        console.warn(`History asset tidak tersedia, menggunakan tampilan offline: ${e?.message || e}`);
        setError('Terjadi kesalahan saat mengambil data history');
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [isOpen, asset]);

  if (!isOpen || !asset) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('id-ID', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusPenangananStyle = (status) => {
    const statusMap = {
      'tunggu': { label: 'Menunggu', bg: '#fef3c7', color: '#92400e' },
      'tproses': { label: 'Di Proses (GA)', bg: '#dbeafe', color: '#1e40af' },
      'sedang': { label: 'Di Ajukan (Cashier)', bg: '#f3e8ff', color: '#581c87' },
      'acc': { label: 'Di ACC', bg: '#c7f0d8', color: '#065f46' },
      's titangani': { label: 'Ditangani (Vendor)', bg: '#fed7aa', color: '#b45309' },
      's ready': { label: 'Ready (Selesai)', bg: '#dcfce7', color: '#166534' },
      's pending': { label: 'Pending (ACC Management)', bg: '#fce7f3', color: '#be185d' },
      'received': { label: 'Selesai', bg: '#a7f3d0', color: '#047857' },
    };
    
    // Remove extra spaces from status
    const cleanStatus = (status || '').trim().toLowerCase();
    const mapped = statusMap[cleanStatus];
    
    if (mapped) {
      return { label: mapped.label, bg: mapped.bg, color: mapped.color };
    }
    
    // Default fallback
    return { label: 'Ngantri', bg: '#f3f4f6', color: '#6b7280' };
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)',
      zIndex: 9000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    }}>
      <motion.div
        initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        style={{
          backgroundColor: '#fff', borderRadius: '24px 24px 0 0',
          padding: '28px 24px 36px', width: '100%', maxWidth: 520,
          maxHeight: '90vh', overflowY: 'auto',
        }}
      >
        <div style={{ width: 40, height: 4, borderRadius: 2, backgroundColor: '#e2e8f0', margin: '0 auto 20px' }} />

        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 18, fontWeight: 800, color: '#1e293b', marginBottom: 4 }}>🕐 History Asset</h3>
          <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>{asset.kode} — {asset.nama}</p>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '32px', color: '#94a3b8' }}>
            <div style={{ fontSize: 24, marginBottom: 12 }}>⏳</div>
            <p>Memuat history...</p>
          </div>
        )}

        {error && (
          <div style={{
            backgroundColor: '#fee2e2', borderRadius: 12, padding: '12px 14px',
            marginBottom: 20, border: '1px solid #fecaca', color: '#991b1b',
            fontSize: 13,
          }}>
            ❌ {error}
          </div>
        )}

        {!loading && history && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Sektor: PEMBELIAN */}
            {history.data_pembelian && (
              <div style={{ backgroundColor: '#f8fafc', borderRadius: 16, padding: '14px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <span style={{ fontSize: 20 }}>💳</span>
                  <h4 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: 0 }}>History Pembelian</h4>
                </div>

                {history.data_pembelian.header ? (
                  <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: '12px', border: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 12 }}>
                      <div>
                        <div style={{ color: '#94a3b8', fontWeight: 600, marginBottom: 3 }}>PO ID</div>
                        <div style={{ color: '#1e293b', fontFamily: 'monospace', fontWeight: 600 }}>{history.data_pembelian.header.id}</div>
                      </div>
                      <div>
                        <div style={{ color: '#94a3b8', fontWeight: 600, marginBottom: 3 }}>Tanggal</div>
                        <div style={{ color: '#1e293b', fontWeight: 600 }}>{formatDate(history.data_pembelian.header.created_at)}</div>
                      </div>
                      <div>
                        <div style={{ color: '#94a3b8', fontWeight: 600, marginBottom: 3 }}>Total</div>
                        <div style={{ color: '#1e293b', fontWeight: 600 }}>
                          Rp {Number(history.data_pembelian.header.total_amount || 0).toLocaleString('id-ID')}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: '#94a3b8', fontWeight: 600, marginBottom: 3 }}>Status</div>
                        <div style={{ color: '#1e293b', fontWeight: 600 }}>{history.data_pembelian.header.status || '-'}</div>
                      </div>
                    </div>

                    {history.data_pembelian.detail && history.data_pembelian.detail.length > 0 && (
                      <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #e5e7eb' }}>
                        <div style={{ color: '#94a3b8', fontWeight: 600, fontSize: 11, marginBottom: 8 }}>DETAIL PEMBELIAN</div>
                        {history.data_pembelian.detail.map((d, i) => (
                          <div key={i} style={{ fontSize: 11, color: '#1e293b', marginBottom: 4, padding: '6px', backgroundColor: '#f1f5f9', borderRadius: 6 }}>
                            <div>📦 {d.deskripsi || d.barang_nama || '-'}</div>
                            <div style={{ color: '#64748b', marginTop: 2 }}>
                              Qty: {d.quantity} × Rp {Number(d.unit_price || 0).toLocaleString('id-ID')}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ color: '#94a3b8', fontSize: 12 }}>Tidak ada data pembelian</div>
                )}
              </div>
            )}

            {/* Sektor: PENANGANAN */}
            {history.data_penanganan && history.data_penanganan.length > 0 && (
              <div style={{ backgroundColor: '#f8fafc', borderRadius: 16, padding: '14px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <span style={{ fontSize: 20 }}>🔧</span>
                  <h4 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: 0 }}>History Penanganan ({history.data_penanganan.length})</h4>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {history.data_penanganan
                    .slice(0, expandPenanganan ? undefined : ITEMS_LIMIT)
                    .map((p, i) => (
                      <div key={i} style={{
                        backgroundColor: '#fff', borderRadius: 12, padding: '12px',
                        border: '1px solid #e5e7eb', fontSize: 12,
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                          <div>
                            <span style={{ fontWeight: 700, color: '#1e293b' }}>Jenis: </span>
                            <span style={{ color: '#64748b' }}>{p.jenis_penanganan || p.jenis || '-'}</span>
                          </div>
                          {(() => {
                            const statusStyle = getStatusPenangananStyle(p.status);
                            return (
                              <span style={{
                                fontSize: 10, fontWeight: 700, padding: '4px 8px',
                                borderRadius: 6, backgroundColor: statusStyle.bg, color: statusStyle.color,
                              }}>
                                {statusStyle.label}
                              </span>
                            );
                          })()}
                        </div>
                        <div style={{ color: '#94a3b8', marginBottom: 3 }}>📅 {formatDate(p.created_at)}</div>
                        <div style={{ color: '#64748b', marginTop: 4 }}>
                          {p.deskripsi && <div>💬 {p.deskripsi}</div>}
                          {p.keterangan && <div style={{ marginTop: 2 }}>📝 {p.keterangan}</div>}
                        </div>
                      </div>
                    ))}
                  
                  {history.data_penanganan.length > ITEMS_LIMIT && !expandPenanganan && (
                    <button
                      onClick={() => setExpandPenanganan(true)}
                      style={{
                        backgroundColor: '#f0f9ff', borderRadius: 12, padding: '12px',
                        border: '1px dashed #0284c7', color: '#0369a1', fontSize: 12,
                        fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s',
                      }}
                      onMouseDown={e => e.currentTarget.style.transform = 'scale(0.98)'}
                      onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
                    >
                      📂 Tampilkan {history.data_penanganan.length - ITEMS_LIMIT} data lainnya
                    </button>
                  )}

                  {expandPenanganan && history.data_penanganan.length > ITEMS_LIMIT && (
                    <button
                      onClick={() => setExpandPenanganan(false)}
                      style={{
                        backgroundColor: '#f8fafc', borderRadius: 12, padding: '12px',
                        border: '1px dashed #cbd5e1', color: '#64748b', fontSize: 12,
                        fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s',
                      }}
                      onMouseDown={e => e.currentTarget.style.transform = 'scale(0.98)'}
                      onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
                    >
                      📂 Sembunyikan
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Sektor: PERPINDAHAN */}
            {history.data_perpindahan && history.data_perpindahan.length > 0 && (
              <div style={{ backgroundColor: '#f8fafc', borderRadius: 16, padding: '14px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <span style={{ fontSize: 20 }}>📍</span>
                  <h4 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: 0 }}>History Perpindahan ({history.data_perpindahan.length})</h4>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {history.data_perpindahan
                    .slice(0, expandPerpindahan ? undefined : ITEMS_LIMIT)
                    .map((h, i) => (
                      <div key={i} style={{
                        backgroundColor: '#fff', borderRadius: 12, padding: '12px',
                        border: '1px solid #e5e7eb', fontSize: 12,
                      }}>
                        <div style={{ marginBottom: 8 }}>
                          <div style={{ color: '#94a3b8', fontWeight: 600, marginBottom: 2 }}>Dari:</div>
                          <div style={{ color: '#1e293b', fontWeight: 600 }}>📍 {h.from_room_name || h.from_lokasi || 'N/A'}</div>
                        </div>
                        <div style={{ marginBottom: 8 }}>
                          <div style={{ color: '#94a3b8', fontWeight: 600, marginBottom: 2 }}>Ke:</div>
                          <div style={{ color: '#1e293b', fontWeight: 600 }}>📍 {h.to_room_name || h.to_lokasi || 'N/A'}</div>
                        </div>
                        {h.moved_by_name && (
                          <div style={{ marginBottom: 6 }}>
                            <div style={{ color: '#94a3b8', fontWeight: 600, marginBottom: 2 }}>Dipindahkan oleh:</div>
                            <div style={{ color: '#1e293b' }}>👤 {h.moved_by_name}</div>
                          </div>
                        )}
                        <div style={{ color: '#94a3b8' }}>
                          📅 {formatDate(h.created_at)}
                        </div>
                        {h.notes && (
                          <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid #e5e7eb', color: '#64748b', fontStyle: 'italic' }}>
                            📝 {h.notes}
                          </div>
                        )}
                      </div>
                    ))}

                  {history.data_perpindahan.length > ITEMS_LIMIT && !expandPerpindahan && (
                    <button
                      onClick={() => setExpandPerpindahan(true)}
                      style={{
                        backgroundColor: '#f0f9ff', borderRadius: 12, padding: '12px',
                        border: '1px dashed #0284c7', color: '#0369a1', fontSize: 12,
                        fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s',
                      }}
                      onMouseDown={e => e.currentTarget.style.transform = 'scale(0.98)'}
                      onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
                    >
                      📂 Tampilkan {history.data_perpindahan.length - ITEMS_LIMIT} data lainnya
                    </button>
                  )}

                  {expandPerpindahan && history.data_perpindahan.length > ITEMS_LIMIT && (
                    <button
                      onClick={() => setExpandPerpindahan(false)}
                      style={{
                        backgroundColor: '#f8fafc', borderRadius: 12, padding: '12px',
                        border: '1px dashed #cbd5e1', color: '#64748b', fontSize: 12,
                        fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s',
                      }}
                      onMouseDown={e => e.currentTarget.style.transform = 'scale(0.98)'}
                      onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
                    >
                      📂 Sembunyikan
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Empty state */}
            {!history.data_pembelian && (!history.data_penanganan || history.data_penanganan.length === 0) && (!history.data_perpindahan || history.data_perpindahan.length === 0) && (
              <div style={{ textAlign: 'center', padding: '32px 20px' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
                <p style={{ fontWeight: 600, color: '#4b5563', margin: 0 }}>Belum ada history</p>
                <p style={{ color: '#9ca3af', fontSize: 12, margin: '4px 0 0' }}>Asset ini belum memiliki riwayat pembelian, penanganan, atau perpindahan</p>
              </div>
            )}
          </div>
        )}

        <button
          onClick={onClose}
          style={{
            width: '100%', padding: '13px', backgroundColor: '#f8fafc',
            color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 16,
            fontWeight: 500, fontSize: 14, cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 20,
          }}
        >
          <span>✕</span> Tutup
        </button>
      </motion.div>
    </div>
  );
};

// ── Modal Detail Asset ────────────────────────────────────────────────────────
const AssetDetailModal = ({ asset, onClose, onAjukanPenanganan, onPindahkanLokasi, onCekHistory }) => {
  if (!asset) return null;

  const karyawan = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
  const jabatan = (karyawan.jabatan || '').toLowerCase();
  const canPindah = jabatan.includes('general affair') || jabatan.includes('it');

  const { bg: kondisiBg, color: kondisiColor } = getKondisiStyle(asset.kondisi);

  const infoItems = [
    ['📦 Nama', asset.nama],
    ['🏷️ Kategori', asset.kategori],
    ['📍 Lokasi', asset.lokasi],
    ['👤 Pemegang', asset.pemegang || '-'],
    ['📅 Tgl Beli', asset.tanggal_beli ? new Date(asset.tanggal_beli).toLocaleDateString('id-ID') : '-'],
    ['🔢 Serial', asset.serial_number || '-'],
  ];

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)',
      zIndex: 9000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    }}>
      <motion.div
        initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        style={{
          backgroundColor: '#fff', borderRadius: '24px 24px 0 0',
          padding: '28px 24px 36px', width: '100%', maxWidth: 520,
          maxHeight: '85vh', overflowY: 'auto',
        }}
      >
        <div style={{ width: 40, height: 4, borderRadius: 2, backgroundColor: '#e2e8f0', margin: '0 auto 20px' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginBottom: 4 }}>KODE ASSET</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#1e293b' }}>{asset.kode}</div>
          </div>
          <span style={{
            fontSize: 11, fontWeight: 700, padding: '4px 10px',
            borderRadius: 10, backgroundColor: kondisiBg, color: kondisiColor,
          }}>
            {asset.kondisi || 'N/A'}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
          {infoItems.map(([label, value]) => (
            <div key={label} style={{ backgroundColor: '#f8fafc', borderRadius: 12, padding: '12px 14px' }}>
              <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{value || '-'}</div>
            </div>
          ))}
        </div>

        {asset.keterangan && (
          <div style={{
            backgroundColor: '#f0f9ff', borderRadius: 12, padding: '12px 14px',
            marginBottom: 20, border: '1px solid #bae6fd',
          }}>
            <div style={{ fontSize: 11, color: '#0369a1', fontWeight: 600, marginBottom: 4 }}>📝 Keterangan</div>
            <div style={{ fontSize: 13, color: '#0c4a6e' }}>{asset.keterangan}</div>
          </div>
        )}

        {canPindah && (
          <ActionButton
            onClick={() => onPindahkanLokasi(asset)}
            bgColor="#0f6e56"
            iconBg="rgba(255,255,255,0.15)"
            icon="📍"
            title="Pindahkan lokasi asset"
            desc="Ubah lokasi penempatan asset ini"
            titleColor="#e1f5ee"
            descColor="#9fe1cb"
            arrowColor="#9fe1cb"
          />
        )}

        <ActionButton
          onClick={() => onAjukanPenanganan(asset)}
          bgColor="#185fa5"
          iconBg="rgba(255,255,255,0.15)"
          icon="🔧"
          title="Ajukan penanganan"
          desc="Laporkan masalah atau minta perbaikan"
          titleColor="#e6f1fb"
          descColor="#85b7eb"
          arrowColor="#85b7eb"
        />

        <ActionButton
          onClick={() => onCekHistory(asset)}
          bgColor="#fff"
          iconBg="#f1f5f9"
          icon="🕐"
          title="Cek history"
          desc="Lihat riwayat perpindahan & penanganan"
          titleColor="#1e293b"
          descColor="#64748b"
          arrowColor="#94a3b8"
          style={{ border: '1px solid #e2e8f0' }}
        />

        <button
          onClick={onClose}
          style={{
            width: '100%', padding: '13px', backgroundColor: '#f8fafc',
            color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 16,
            fontWeight: 500, fontSize: 14, cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          <span>✕</span> Tutup
        </button>
      </motion.div>
    </div>
  );
};

// ── Form pindah lokasi ────────────────────────────────────────────────────────
const FormPindahLokasi = ({ asset, onClose, onSuccess }) => {
  const [lokasi, setLokasi] = useState('');
  const [locations, setLocations] = useState([]);
  const [loadingLoc, setLoadingLoc] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState('');
  const selectRef = useRef(null);

  useEffect(() => {
    const fetchLocations = async () => {
      setLoadingLoc(true);
      try {
        const res = await fetch(`${API_BASE}/asset-locations`);
        const json = await res.json();
        if (json.status === 'success' && Array.isArray(json.data)) {
          const mapped = json.data.map(loc => ({ id: loc.id, nama: loc.name }));
          const allowed = filterAllowedLocations(mapped);
          setLocations(allowed);

          const currentLocObj = allowed.find(l => l.nama.toLowerCase() === (asset.lokasi || '').toLowerCase());
          if (currentLocObj) {
            setLokasi(currentLocObj.id);
          }
        }
      } catch {
        console.warn('Gagal mengambil daftar lokasi');
      } finally {
        setLoadingLoc(false);
      }
    };
    fetchLocations();
  }, [asset.lokasi]);

  const handleSubmit = async () => {
    if (!lokasi) {
      Swal.fire({ icon: 'warning', title: 'Pilih lokasi baru', timer: 1500, showConfirmButton: false });
      return;
    }
    const selectedLocObj = locations.find(loc => String(loc.id) === String(lokasi));
    const selectedLocName = selectedLocObj ? selectedLocObj.nama : lokasi;

    if (selectedLocName.toLowerCase() === (asset.lokasi || '').toLowerCase()) {
      Swal.fire({ icon: 'info', title: 'Lokasi sama', text: 'Silakan pilih lokasi yang berbeda dari lokasi saat ini.', timer: 1800, showConfirmButton: false });
      return;
    }

    setSubmitting(true);
    try {
      const karyawan = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
      const fromLocObj = locations.find(loc => loc.nama.toLowerCase() === (asset.lokasi || '').toLowerCase());
      const from_room_id = fromLocObj ? fromLocObj.id : null;
      const to_room_id = selectedLocObj ? selectedLocObj.id : lokasi;

      await updateAssetLocation(asset.kode, selectedLocName);
      const res = await fetch(`${API_BASE}/assets/${asset.kode}/pindah-lokasi`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lokasi: selectedLocName,
          asset_id: asset.id,
          from_room_id: from_room_id,
          to_room_id: to_room_id,
          moved_by: karyawan.id || null,
          notes: notes,
        }),
      });
      const json = await res.json();
      if (json.status === 'success') {
        await Swal.fire({ icon: 'success', title: 'Lokasi Dipindahkan!', text: `Asset berhasil dipindahkan ke: ${selectedLocName}`, timer: 2000, showConfirmButton: false });
        onSuccess(selectedLocName);
      } else {
        throw new Error(json.message || 'Gagal update lokasi di server');
      }
    } catch {
      await Swal.fire({ icon: 'success', title: 'Disimpan Lokal (Offline)', text: 'Lokasi di-cache secara lokal, akan disinkronkan saat online.', timer: 2500, showConfirmButton: false });
      onSuccess(selectedLocName);
    } finally {
      setSubmitting(false);
    }
  };

  const btnBase = { width: '100%', border: 'none', borderRadius: 16, fontWeight: 700, fontSize: 15, cursor: 'pointer' };

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 9100, display: 'flex', alignItems: 'flex-end' }}>
      <motion.div
        initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        style={{ backgroundColor: '#fff', borderRadius: '24px 24px 0 0', padding: '28px 24px 36px', width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', margin: '0 auto' }}
      >
        <div style={{ width: 40, height: 4, borderRadius: 2, backgroundColor: '#e2e8f0', margin: '0 auto 20px' }} />
        <h3 style={{ fontSize: 18, fontWeight: 800, color: '#1e293b', marginBottom: 4 }}>📍 Pindahkan Lokasi Asset</h3>
        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>{asset.kode} — {asset.nama}</p>

        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8 }}>Pilih Lokasi Baru *</div>
          {loadingLoc ? (
            <div style={{ fontSize: 12, color: '#94a3b8' }}>Memuat daftar lokasi...</div>
          ) : (
            <div style={{ width: '100%' }}>
              <select ref={selectRef} autoComplete="off" defaultValue={lokasi} style={{ width: '100%' }}>
                <option value="">Cari dan pilih...</option>
              </select>
              <TomSelectField
                selectRef={selectRef}
                options={locations}
                onChange={(val) => setLokasi(val)}
                placeholder="Cari dan pilih..."
              />
            </div>
          )}
        </div>

        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8 }}>Keterangan / Notes</div>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Tuliskan catatan pemindahan (opsional)..."
            rows={3}
            style={{
              width: '100%', padding: '12px 14px', borderRadius: 12,
              border: '1px solid #e2e8f0', fontSize: 14, fontFamily: 'inherit',
              outline: 'none', boxSizing: 'border-box', backgroundColor: '#f8fafc',
              resize: 'vertical',
            }}
          />
        </div>

        <button
          onClick={handleSubmit}
          disabled={submitting}
          style={{ ...btnBase, padding: '16px', marginBottom: 12, backgroundColor: submitting ? '#a7f3d0' : '#10b981', color: '#fff', cursor: submitting ? 'not-allowed' : 'pointer' }}
        >
          {submitting ? 'Menyimpan...' : '💾 Simpan Lokasi Baru'}
        </button>
        <button
          onClick={onClose}
          style={{ ...btnBase, padding: '14px', backgroundColor: '#f1f5f9', color: '#64748b' }}
        >
          Batal
        </button>
      </motion.div>
    </div>
  );
};

// ── Form pengajuan penanganan ─────────────────────────────────────────────────
const FormPenanganan = ({ asset, onClose, onSuccess }) => {
  const karyawan = JSON.parse(localStorage.getItem('data_karyawan') || '{}');

  const [tanggal] = useState(new Date().toISOString().split('T')[0]);
  const [dari, setDari] = useState(asset.lokasi || '');
  const [jenis, setJenis] = useState('Service');
  const [perangkat] = useState(asset.nama || '');
  const [penanganan] = useState(asset.keterangan || '');
  const [tag] = useState(asset.kode || '');
  const [problem, setProblem] = useState('');
  const [pelapor, setPelapor] = useState(`${karyawan.nama_depan || ''} ${karyawan.nama_belakang || ''}`.trim());
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!problem.trim()) { Swal.fire({ icon: 'warning', title: 'Keterangan/Problem wajib diisi', timer: 1500, showConfirmButton: false }); return; }
    if (!pelapor.trim()) { Swal.fire({ icon: 'warning', title: 'Nama Pelapor wajib diisi', timer: 1500, showConfirmButton: false }); return; }

    setSubmitting(true);
    try {
      const payload = {
        jenis: 'penanganan',
        jenis_penanganan: jenis,
        kode_asset: tag,
        nama_asset: perangkat,
        kategori_asset: asset.kategori,
        lokasi_asset: dari,
        deskripsi: problem,
        karyawan_id: karyawan.id,
        karyawan_nama: pelapor,
      };

      const id = await savePengajuan(payload);

      try {
        const res = await fetch(`${API_BASE}/asset-pengajuan`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id, tanggal, dari, jenis, id_barang: asset.id, perangkat, penanganan, tag, problem, pelapor, karyawan_id: karyawan.id }),
        });
        if (res.ok) {
          await updatePengajuanStatus(id, 'Pending', true);
        }
      } catch { /* offline — tersimpan di IDB */ }

      await Swal.fire({ icon: 'success', title: 'Pengajuan GA Terkirim!', text: 'Pengajuan penanganan berhasil disimpan.', timer: 2000, showConfirmButton: false });
      onSuccess();
    } catch {
      Swal.fire({ icon: 'error', title: 'Gagal', text: 'Terjadi kesalahan saat menyimpan pengajuan.' });
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = { width: '100%', padding: '12px 14px', borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box', backgroundColor: '#f8fafc' };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: '#94a3b8', marginBottom: 5, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' };

  const identityRows = [
    { label: 'Kode Asset', value: tag || '-', icon: '🏷️', mono: true },
    { label: 'Nama Asset', value: perangkat || '-', icon: '📦' },
    ...(penanganan ? [{ label: 'Keterangan Asset', value: penanganan, icon: '📝' }] : []),
    { label: 'Lokasi / Dari', value: dari || '-', icon: '📍' },
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 9100, display: 'flex', alignItems: 'flex-end' }}>
      <motion.div
        initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        style={{ backgroundColor: '#fff', borderRadius: '24px 24px 0 0', padding: '28px 24px 36px', width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', margin: '0 auto' }}
      >
        <div style={{ width: 40, height: 4, borderRadius: 2, backgroundColor: '#e2e8f0', margin: '0 auto 20px' }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ width: 44, height: 44, borderRadius: 14, backgroundColor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
            🔧
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#1e293b' }}>Buat Pengajuan GA</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Laporkan masalah atau minta penanganan</div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>TANGGAL</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', marginTop: 2 }}>
              {new Date(tanggal).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}
            </div>
          </div>
        </div>

        <div style={{ backgroundColor: '#f8fafc', borderRadius: 18, border: '1px solid #e5e7eb', overflow: 'hidden', marginBottom: 20 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13 }}>🪪</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Identitas Asset</span>
          </div>
          {identityRows.map((row, i) => (
            <div
              key={row.label}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '11px 16px',
                borderBottom: i < identityRows.length - 1 ? '1px solid #f1f5f9' : 'none',
              }}
            >
              <span style={{ fontSize: 14, marginTop: 1, flexShrink: 0 }}>{row.icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, marginBottom: 2 }}>{row.label}</div>
                <div style={{
                  fontSize: 13, fontWeight: 600, color: '#1e293b',
                  ...(row.mono ? { fontFamily: 'monospace' } : {}),
                  wordBreak: 'break-all',
                }}>
                  {row.value}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={labelStyle}>Jenis Penanganan</label>
            <select value={jenis} onChange={e => setJenis(e.target.value)} style={inputStyle}>
              <option value="Service">Service</option>
              <option value="Pembelian Sparepart">Pembelian Sparepart</option>
              <option value="Pajak">Pajak</option>
              <option value="KIR">KIR</option>
              <option value="Tagihan">Tagihan ( Internet, Listrik )</option>
              <option value="Isi Ulang">Isi Ulang ( Apar, Token )</option>
              <option value="Tera Ulang">Tera Ulang ( Timbangan )</option>
              <option value="Ganti Baru">Ganti Baru</option>
              <option value="Pengadaan">Pengadaan</option>
              <option value="Lainya">Lainya</option>
            </select>
          </div>

          <div>
            <label style={labelStyle}>Deskripsi Problem <span style={{ color: '#ef4444' }}>*</span></label>
            <textarea
              value={problem}
              onChange={e => setProblem(e.target.value)}
              placeholder="Tuliskan keluhan atau detail problem pada asset..."
              rows={4}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          <div>
            <label style={labelStyle}>Nama Pelapor <span style={{ color: '#ef4444' }}>*</span></label>
            <input
              type="text"
              value={pelapor}
              onChange={e => setPelapor(e.target.value)}
              style={inputStyle}
              placeholder="Nama lengkap pelapor..."
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={{ width: '100%', padding: '16px', marginBottom: 12, backgroundColor: submitting ? '#93c5fd' : '#2563eb', color: '#fff', border: 'none', borderRadius: 16, fontWeight: 700, fontSize: 16, cursor: submitting ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}
            >
              {submitting ? 'Mengirim...' : '📤 Kirim Pengajuan'}
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{ width: '100%', padding: '14px', backgroundColor: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: 16, fontWeight: 600, fontSize: 15, cursor: 'pointer' }}
            >
              Batal
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// ── Form pengajuan pengadaan ──────────────────────────────────────────────────
const FormPengadaan = ({ onSuccess }) => {
  const karyawan = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
  const dataUser = JSON.parse(localStorage.getItem('data_user') || '{}');
  const isAgen = !!karyawan.is_agen || !!dataUser.is_agen ||
    String(karyawan.role || '').toLowerCase() === 'agen' ||
    String(dataUser.role || '').toLowerCase() === 'agen' ||
    localStorage.getItem('is_agen') === 'true' ||
    (Boolean(karyawan.nik) && !String(karyawan.nik).toUpperCase().startsWith('CXP'));
  const agenNama = karyawan.agen || karyawan.nama_agen || dataUser.agen || dataUser.nama_agen || karyawan.kantor || '';
  const namaLengkap = `${karyawan.nama_depan || ''} ${karyawan.nama_belakang || ''}`.trim();
  const namaPelapor = (isAgen && agenNama) ? agenNama : (namaLengkap || karyawan.nama || 'Pelapor');
  const tanggal = new Date().toISOString().split('T')[0];

  const [dari, setDari] = useState('');
  const [nama, setNama] = useState('');
  const [keterangan, setKeterangan] = useState('');
  const [pelapor, setPelapor] = useState(namaPelapor);
  const [submitting, setSubmitting] = useState(false);
  const [loadingLokasi, setLoadingLokasi] = useState(true);
  const [lokasiOptions, setLokasiOptions] = useState([]);
  const selectRef = useRef(null);

  const matchLokasi = (locations) => {
    if (isAgen && agenNama) {
      const raw = agenNama.toLowerCase().trim();
      const clean = raw.replace(/\b(agen|jne|kantor|cabang|pos|kp)\b/gi, '').trim();
      const keywords = clean.split(/\s+/).filter(k => k.length >= 3);
      const matchedAgen = locations.filter(loc => {
        const locLower = (loc.nama || '').toLowerCase().trim();
        if (locLower === raw) return true;
        if (raw.length >= 3 && (locLower.includes(raw) || raw.includes(locLower))) return true;
        if (clean.length >= 3 && (locLower.includes(clean) || clean.includes(locLower))) return true;
        if (keywords.length > 0 && keywords.every(kw => locLower.includes(kw))) return true;
        return false;
      });
      if (matchedAgen.length > 0) return matchedAgen;
    }

    const kantor = (karyawan.kantor || '').toLowerCase();
    const jabatan = (karyawan.jabatan || '').toLowerCase();
    const divisi = (karyawan.divisi || karyawan.bagian || karyawan.departemen || '').toLowerCase();
    const allInfo = `${jabatan} ${divisi}`;

    const isAdmin = jabatan.includes('general affair') || jabatan.includes('it');
    if (isAdmin) return locations;

    const keywordMap = [
      ['customer service', ['customer service', 'ruang cs', 'ruang customer']],
      ['marketing', ['marketing', 'pemasaran', 'ruang marketing']],
      ['finance', ['finance', 'keuangan', 'ruang keuangan']],
      ['hrd', ['hrd', 'hr ', ' hr', 'sdm', 'personalia', 'ruang hrd']],
      ['operasional', ['operasional', 'operational', 'ruang operasional']],
      ['accounting', ['accounting', 'akuntansi', 'ruang accounting']],
      ['sales', ['sales', 'penjualan', 'ruang sales']],
      ['gudang', ['gudang', 'warehouse', 'ruang gudang']],
      ['pickup', ['pickup', 'kurir', 'area pickup']],
      ['delivery', ['delivery', 'pengiriman', 'area delivery']],
      ['admin', ['admin', 'administrasi', 'ruang admin']],
      ['kepala', ['kepala', 'pimpinan', 'ruang kepala', 'ruang pimpinan']],
      ['manager', ['manager', 'manajer', 'ruang manager']],
    ];

    const activeKeywords = [];
    for (const [key, aliases] of keywordMap) {
      if (allInfo.includes(key)) activeKeywords.push(...aliases);
    }

    const filtered = locations.filter(loc => {
      const locLower = loc.nama.toLowerCase();
      if (kantor && locLower.includes(kantor)) return true;
      if (activeKeywords.some(kw => locLower.includes(kw))) return true;
      return false;
    });

    return filtered.length > 0 ? filtered : locations;
  };

  useEffect(() => {
    const fetchLokasi = async () => {
      setLoadingLokasi(true);
      try {
        const res = await fetch(`${API_BASE}/asset-locations`);
        const json = await res.json();
        if (json.status === 'success' && Array.isArray(json.data)) {
          const mapped = json.data.map(loc => ({ id: loc.id, nama: loc.name }));
          const matched = filterAllowedLocations(matchLokasi(mapped));
          setLokasiOptions(matched);
          if (matched.length === 1) {
            setDari(matched[0].id);
          } else {
            const targetLocName = (isAgen ? agenNama : karyawan.kantor || '').toLowerCase();
            const exact = matched.find(loc =>
              loc.nama.toLowerCase().includes(targetLocName)
            );
            if (exact) setDari(exact.id);
          }
        }
      } catch {
        console.warn('Gagal ambil daftar lokasi');
      } finally {
        setLoadingLokasi(false);
      }
    };
    fetchLokasi();
  }, []);

  const handleSubmit = async () => {
    if (!String(dari).trim()) { Swal.fire({ icon: 'warning', title: 'Kolom "Dari" wajib diisi', timer: 1500, showConfirmButton: false }); return; }
    if (!nama.trim()) { Swal.fire({ icon: 'warning', title: 'Nama asset wajib diisi', timer: 1500, showConfirmButton: false }); return; }
    if (!pelapor.trim()) { Swal.fire({ icon: 'warning', title: 'Nama pelapor wajib diisi', timer: 1500, showConfirmButton: false }); return; }

    const selectedLocObj = lokasiOptions.find(loc => String(loc.id) === String(dari));
    const selectedLocName = selectedLocObj ? selectedLocObj.nama : dari;

    setSubmitting(true);
    try {
      const payload = {
        jenis: 'penanganan',
        jenis_penanganan: 'pengadaan',
        kode_asset: ' ',
        nama_asset: nama,
        kategori_asset: ' ',
        lokasi_asset: selectedLocName,
        lokasi_asset_id: selectedLocObj ? selectedLocObj.id : null,
        deskripsi: keterangan || `Pengadaan: ${nama}`,
        karyawan_id: karyawan.id,
        karyawan_nama: pelapor,
      };

      const id = await savePengajuan(payload);

      try {
        const res = await fetch(`${API_BASE}/asset-pengajuan`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id, tanggal, dari: selectedLocName, dari_id: selectedLocObj ? selectedLocObj.id : null, jenis: 'pengadaan',
            id_barang: null, perangkat: nama,
            penanganan: ' ', tag: ' ',
            problem: keterangan,
            pelapor, karyawan_id: karyawan.id,
          }),
        });
        if (res.ok) {
          await updatePengajuanStatus(id, 'Pending', true);
        }
      } catch { /* offline */ }

      await Swal.fire({ icon: 'success', title: 'Pengajuan Pengadaan Terkirim!', text: 'Akan segera diproses oleh tim terkait.', timer: 2000, showConfirmButton: false });
      setNama(''); setKeterangan(''); setDari('');
      onSuccess?.();
    } catch {
      Swal.fire({ icon: 'error', title: 'Gagal', text: 'Terjadi kesalahan saat menyimpan pengajuan.' });
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = { width: '100%', padding: '12px 14px', borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box', backgroundColor: '#f8fafc' };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: '#94a3b8', marginBottom: 5, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <label style={labelStyle}>Dari / Lokasi <span style={{ color: '#ef4444' }}>*</span></label>
        {loadingLokasi ? (
          <div style={{ ...inputStyle, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12 }}>⏳</span> Memuat daftar lokasi...
          </div>
        ) : (
          <div style={{ width: '100%' }}>
            <select ref={selectRef} autoComplete="off" defaultValue={dari} style={{ width: '100%' }}>
              <option value="">Cari dan pilih...</option>
            </select>
            <TomSelectField
              selectRef={selectRef}
              options={lokasiOptions}
              onChange={(val) => setDari(val)}
              placeholder="Cari dan pilih..."
            />
          </div>
        )}
        {karyawan.kantor && (
          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
            📍 Kantor terdaftar: <strong>{karyawan.kantor}</strong>
          </div>
        )}
      </div>

      <div>
        <label style={labelStyle}>Nama Asset yang Dibutuhkan <span style={{ color: '#ef4444' }}>*</span></label>
        <input
          value={nama}
          onChange={e => setNama(e.target.value)}
          placeholder="Contoh: Printer Canon IP2870"
          style={inputStyle}
        />
      </div>

      <div>
        <label style={labelStyle}>Deskripsi Problem <span style={{ color: '#ef4444' }}>*</span></label>
        <textarea
          value={keterangan}
          onChange={e => setKeterangan(e.target.value)}
          placeholder="Jelaskan kebutuhan atau alasan pengadaan..."
          rows={4}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </div>

      <div>
        <label style={labelStyle}>Nama Pelapor <span style={{ color: '#ef4444' }}>*</span></label>
        <input
          type="text"
          value={pelapor}
          onChange={e => setPelapor(e.target.value)}
          placeholder="Nama lengkap pelapor..."
          style={inputStyle}
        />
      </div>

      <button
        onClick={handleSubmit}
        disabled={submitting}
        style={{ padding: '15px', backgroundColor: submitting ? '#a78bfa' : '#7c3aed', color: '#fff', border: 'none', borderRadius: 16, fontWeight: 700, fontSize: 15, cursor: submitting ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}
      >
        {submitting ? 'Mengirim...' : '📋 Kirim Pengajuan Pengadaan'}
      </button>
    </div>
  );
};

// ── Kartu riwayat pengajuan ───────────────────────────────────────────────────
const PengajuanCard = ({ p }) => {
  const jenis = p.jenis_penanganan || 'Lainya';
  const cfg = JENIS_CONFIG[jenis] || JENIS_CONFIG['Lainya'];

  return (
    <motion.div
      key={p.id}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        backgroundColor: '#fff', borderRadius: 16, padding: '14px 16px',
        border: '0.5px solid #e2e8f0', marginBottom: 10,
      }}
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div style={{
          width: 40, height: 40, borderRadius: 12, backgroundColor: cfg.bg,
          color: cfg.color, display: 'flex', alignItems: 'center',
          justifyContent: 'center', flexShrink: 0, fontSize: 20,
        }}>
          <i className={`ti ${cfg.icon}`} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
            <div>
              <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>
                {p.jenis === 'pengadaan' ? 'Pengadaan' : 'Pengajuan'} - {jenis}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                {p.nama_asset || p.kode_asset || '-'}
              </div>
            </div>
            <StatusBadge status={p.status} />
          </div>

          {(p.deskripsi || p.alasan) && (
            <div style={{
              fontSize: 12, color: '#64748b', backgroundColor: '#f8fafc',
              borderLeft: '2px solid #e2e8f0', borderRadius: '0 8px 8px 0',
              padding: '8px 10px', marginTop: 8,
            }}>
              {p.deskripsi || p.alasan}
            </div>
          )}

          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginTop: 10, paddingTop: 10, borderTop: '0.5px solid #f1f5f9',
          }}>
            <span style={{ fontSize: 11, color: '#cbd5e1', display: 'flex', alignItems: 'center', gap: 4 }}>
              <i className="ti ti-calendar" style={{ fontSize: 13 }} />
              {new Date(p.created_at).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' })}
            </span>
            {!p.synced && (
              <span style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                <i className="ti ti-wifi-off" style={{ fontSize: 12 }} /> Belum sinkron
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ── Modal Scanner Barcode ─────────────────────────────────────────────────────
const AssetScannerModal = ({ isOpen, onClose, onScanSuccess }) => {
  const [error, setError] = useState('');
  const [flashOn, setFlashOn] = useState(false);
  const [flashSupported, setFlashSupported] = useState(false);

  const codeReaderRef = useRef(null);
  const videoTrackRef = useRef(null);
  const activeStreamRef = useRef(null);
  const activeVideoRef = useRef(null);
  const readerContainerRef = useRef(null);
  const containerId = 'asset-scan-reader';

  const stopCamera = () => {
    if (activeStreamRef.current) {
      activeStreamRef.current.getTracks().forEach(track => {
        try { track.stop(); } catch (e) { }
      });
      activeStreamRef.current = null;
    }
    if (activeVideoRef.current) {
      try {
        activeVideoRef.current.pause();
        activeVideoRef.current.srcObject = null;
      } catch (e) { }
      activeVideoRef.current = null;
    }
    if (codeReaderRef.current) {
      try { codeReaderRef.current.reset(); } catch (e) { }
      codeReaderRef.current = null;
    }
    videoTrackRef.current = null;
  };

  useEffect(() => {
    if (!isOpen) {
      stopCamera();
      return;
    }

    let isMounted = true;
    let codeReader = null;

    const startScanner = async () => {
      try {
        await new Promise(resolve => setTimeout(resolve, 300));
        const element = document.getElementById(containerId);
        if (!element) {
          if (isMounted) setError('Elemen scanner tidak ditemukan.');
          return;
        }
        element.innerHTML = '';
        if (isMounted) setError('');

        codeReader = new BrowserMultiFormatReader();
        codeReaderRef.current = codeReader;

        const onDecodeResult = (result) => {
          if (result?.getText && isMounted) {
            onScanSuccess(result.getText());
            stopCamera();
          }
        };

        const captureStreamInfo = (videoEl) => {
          try {
            const stream = videoEl.srcObject;
            if (stream) {
              activeStreamRef.current = stream;
              const track = stream.getVideoTracks()[0];
              videoTrackRef.current = track;
              const caps = track.getCapabilities?.() || {};
              if (caps.torch) setFlashSupported(true);
            }
          } catch (e) { }
        };

        const constraintList = [
          { audio: false, video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } } },
          { audio: false, video: { facingMode: 'environment' } },
          { audio: false, video: true },
        ];

        let started = false;
        for (const c of constraintList) {
          if (!isMounted) break;
          try {
            element.innerHTML = '';
            const vid = document.createElement('video');
            vid.setAttribute('autoplay', 'true');
            vid.setAttribute('muted', 'true');
            vid.setAttribute('playsinline', 'true');
            Object.assign(vid.style, { width: '100%', height: '100%', display: 'block', objectFit: 'cover', background: '#000' });
            element.appendChild(vid);
            activeVideoRef.current = vid;

            await codeReader.decodeFromConstraints(c, vid, onDecodeResult);
            captureStreamInfo(vid);
            started = true;
            break;
          } catch (err) {
            console.warn('Camera constraint failed:', c, err);
          }
        }

        if (!started && isMounted) {
          setError('Kamera tidak dapat diakses atau izin ditolak.');
        }
      } catch (err) {
        if (isMounted) setError('Gagal memulai scanner.');
      }
    };

    startScanner();

    return () => {
      isMounted = false;
      stopCamera();
    };
  }, [isOpen]);

  const toggleFlash = async () => {
    const track = videoTrackRef.current;
    if (!track) return;
    try {
      const newState = !flashOn;
      await track.applyConstraints({ advanced: [{ torch: newState }] });
      setFlashOn(newState);
    } catch (e) {
      console.warn('Flash toggle failed:', e);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)',
      zIndex: 9500, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        position: 'relative', width: '90%', maxWidth: 400, height: 400,
        backgroundColor: '#000', borderRadius: 24, overflow: 'hidden',
        boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
      }}>
        <div ref={readerContainerRef} id={containerId} style={{ width: '100%', height: '100%' }} />

        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10,
        }}>
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)',
            WebkitMaskImage: 'radial-gradient(ellipse 200px 200px at 50% 50%, transparent 0%, black 100%)',
            maskImage: 'radial-gradient(ellipse 200px 200px at 50% 50%, transparent 0%, black 100%)',
          }} />

          {[
            { top: 'calc(50% - 100px)', left: 'calc(50% - 100px)', borderTop: '3px solid #3b82f6', borderLeft: '3px solid #3b82f6', borderRadius: '12px 0 0 0' },
            { top: 'calc(50% - 100px)', right: 'calc(50% - 100px)', borderTop: '3px solid #3b82f6', borderRight: '3px solid #3b82f6', borderRadius: '0 12px 0 0' },
            { bottom: 'calc(50% - 100px)', left: 'calc(50% - 100px)', borderBottom: '3px solid #3b82f6', borderLeft: '3px solid #3b82f6', borderRadius: '0 0 0 12px' },
            { bottom: 'calc(50% - 100px)', right: 'calc(50% - 100px)', borderBottom: '3px solid #3b82f6', borderRight: '3px solid #3b82f6', borderRadius: '0 0 12px 0' },
          ].map((s, i) => (
            <div key={i} style={{ position: 'absolute', width: 30, height: 30, ...s }} />
          ))}

          <div style={{
            position: 'absolute',
            left: '50%', transform: 'translateX(-50%)',
            width: 200, height: 2,
            background: 'linear-gradient(to right, transparent, #3b82f6, transparent)',
            animation: 'scanline 2s ease-in-out infinite',
            borderRadius: 2,
          }} />
        </div>

        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: 16, right: 16, zIndex: 20,
            width: 36, height: 36, borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.25)', border: 'none',
            color: '#fff', fontSize: 16, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          ✕
        </button>

        {flashSupported && (
          <button
            onClick={toggleFlash}
            style={{
              position: 'absolute', bottom: 16, right: 16, zIndex: 20,
              width: 36, height: 36, borderRadius: '50%',
              backgroundColor: flashOn ? 'rgba(250,204,21,0.4)' : 'rgba(255,255,255,0.25)',
              border: 'none', color: flashOn ? '#facc15' : '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            ⚡
          </button>
        )}
      </div>

      <div style={{ color: '#fff', marginTop: 20, textAlign: 'center', padding: '0 24px', zIndex: 20 }}>
        <p style={{ fontWeight: 600, margin: '0 0 8px 0', fontSize: 14 }}>Arahkan Kamera ke Barcode / QR Asset</p>
        {error ? (
          <p style={{ color: '#f59e0b', fontSize: 12, margin: 0 }}>{error}</p>
        ) : (
          <p style={{ color: '#94a3b8', fontSize: 12, margin: 0 }}>Pastikan QR code berada di dalam bingkai area scan</p>
        )}
      </div>

      <style>{`
        @keyframes scanline {
          0%   { top: calc(50% - 90px); opacity: 1; }
          50%  { top: calc(50% + 90px); opacity: 1; }
          100% { top: calc(50% - 90px); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

// ── Komponen utama AssetTab ───────────────────────────────────────────────────
const AssetTab = () => {
  const [subTab, setSubTab] = useState('cek');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [allCount, setAllCount] = useState(0);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showFormPenanganan, setShowFormPenanganan] = useState(false);
  const [showFormPindah, setShowFormPindah] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyAsset, setHistoryAsset] = useState(null);
  const [pendingAsset, setPendingAsset] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [pengajuanList, setPengajuanList] = useState([]);
  const [loadingPengajuan, setLoadingPengajuan] = useState(false);
  const [unsyncedCount, setUnsyncedCount] = useState(0);
  const [isScannerOpen, setIsScannerOpen] = useState(false);

  const searchTimeout = useRef(null);

  const karyawan = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('data_karyawan') || '{}');
    } catch {
      return {};
    }
  }, []);

  const dataUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('data_user') || '{}');
    } catch {
      return {};
    }
  }, []);

  const isAgen = useMemo(() => {
    return !!karyawan.is_agen || !!dataUser.is_agen ||
      String(karyawan.role || '').toLowerCase() === 'agen' ||
      String(dataUser.role || '').toLowerCase() === 'agen' ||
      localStorage.getItem('is_agen') === 'true' ||
      (Boolean(karyawan.nik) && !String(karyawan.nik).toUpperCase().startsWith('CXP'));
  }, [karyawan, dataUser]);

  const agenNama = useMemo(() => {
    return karyawan.agen || karyawan.nama_agen || dataUser.agen || dataUser.nama_agen || karyawan.kantor || dataUser.kantor || '';
  }, [karyawan, dataUser]);

  // Helper pencocokan lokasi asset dengan nama agen
  const isAssetForAgen = useCallback((asset, targetAgenNama) => {
    if (!asset || !targetAgenNama) return false;
    const raw = String(targetAgenNama).toLowerCase().trim();
    const clean = raw.replace(/\b(agen|jne|kantor|cabang|pos|kp)\b/gi, '').trim();
    const keywords = clean.split(/\s+/).filter(k => k.length >= 3);
    const loc = String(asset.lokasi || '').toLowerCase().trim();
    if (!loc) return false;
    if (loc === raw) return true;
    if (raw.length >= 3 && (loc.includes(raw) || raw.includes(loc))) return true;
    if (clean.length >= 3 && (loc.includes(clean) || clean.includes(loc))) return true;
    if (keywords.length > 0 && keywords.every(kw => loc.includes(kw))) return true;
    return false;
  }, []);

  const filterAssetsByQuery = useCallback((list, q) => {
    if (!q || !q.trim()) return list;
    const lower = q.toLowerCase().trim();
    return list.filter(a =>
      (a.kode || '').toLowerCase().includes(lower) ||
      (a.nama || '').toLowerCase().includes(lower) ||
      (a.kategori || '').toLowerCase().includes(lower) ||
      (a.lokasi || '').toLowerCase().includes(lower) ||
      (a.serial_number || '').toLowerCase().includes(lower) ||
      (a.brand || '').toLowerCase().includes(lower) ||
      (a.tipe || '').toLowerCase().includes(lower)
    );
  }, []);

  const updateUnsyncedCount = useCallback(async () => {
    const list = await getAllPengajuan();
    const unsynced = list.filter(p => !p.synced).length;
    setUnsyncedCount(unsynced);
  }, []);

  const loadFromCache = useCallback(async () => {
    const all = await getAllAssets();
    const scoped = isAgen ? all.filter(a => isAssetForAgen(a, agenNama)) : all;
    setAllCount(scoped.length);
    if (isAgen) {
      setResults(filterAssetsByQuery(scoped, query));
    } else {
      setResults(query.trim() ? filterAssetsByQuery(scoped, query) : []);
    }
    setLastSync(getLastSyncTime());
    updateUnsyncedCount();
  }, [isAgen, agenNama, query, isAssetForAgen, filterAssetsByQuery, updateUnsyncedCount]);

  const syncFromAPI = useCallback(async (showIndicator = false) => {
    if (showIndicator) setSyncing(true);
    try {
      const res = await fetch(`${API_BASE}/assets`);
      const json = await res.json();
      if (json.status === 'success' && Array.isArray(json.data)) {
        await saveAllAssets(json.data);
        const scoped = isAgen ? json.data.filter(a => isAssetForAgen(a, agenNama)) : json.data;
        setAllCount(scoped.length);
        setLastSync(getLastSyncTime());
        if (isAgen) {
          setResults(filterAssetsByQuery(scoped, query));
        } else if (query.trim()) {
          setResults(filterAssetsByQuery(scoped, query));
        } else {
          setResults([]);
        }
      }
    } catch {
      console.warn('[AssetTab] Sync offline, menggunakan cache IDB');
    } finally {
      if (showIndicator) setSyncing(false);
    }
  }, [isAgen, agenNama, query, isAssetForAgen, filterAssetsByQuery]);

  const syncOfflinePengajuan = async () => {
    const all = await getAllPengajuan();
    const unsynced = all.filter(p => !p.synced);
    if (unsynced.length === 0) return 0;

    let successCount = 0;
    for (const p of unsynced) {
      try {
        let bodyData = {};
        if (p.jenis === 'penanganan') {
          bodyData = {
            id: p.id,
            tanggal: p.created_at ? p.created_at.split('T')[0] : new Date().toISOString().split('T')[0],
            dari: p.lokasi_asset,
            jenis: p.jenis_penanganan,
            id_barang: p.id_barang || p.kode_asset,
            perangkat: p.nama_asset,
            penanganan: p.penanganan || '',
            tag: p.kode_asset,
            problem: p.deskripsi,
            pelapor: p.karyawan_nama,
            karyawan_id: p.karyawan_id,
          };
        } else if (p.jenis === 'pengadaan') {
          bodyData = {
            id: p.id,
            jenis: 'pengadaan',
            nama_asset: p.nama_asset,
            kategori: p.kategori,
            jumlah: p.jumlah,
            alasan: p.alasan,
            spesifikasi: p.spesifikasi,
            karyawan_id: p.karyawan_id,
          };
        }
        const res = await fetch(`${API_BASE}/asset-pengajuan`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(bodyData),
        });
        if (res.ok) {
          const json = await res.json();
          if (json.status === 'success' || json.success) {
            await updatePengajuanStatus(p.id, 'Pending', true);
            successCount++;
          }
        }
      } catch (e) {
        console.warn('Gagal sync pengajuan:', p.id, e);
      }
    }
    return successCount;
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    Swal.fire({
      title: 'Sinkronisasi...',
      text: 'Sedang mengirim data offline dan mengupdate list asset...',
      allowOutsideClick: false,
      didOpen: () => { Swal.showLoading(); },
    });

    try {
      const syncedOfflineCount = await syncOfflinePengajuan();
      await syncFromAPI(false);
      await updateUnsyncedCount();

      Swal.fire({
        icon: 'success',
        title: 'Sinkronisasi Berhasil',
        text: syncedOfflineCount > 0
          ? `Berhasil mengirim ${syncedOfflineCount} data offline & memperbarui asset.`
          : 'Data asset berhasil diperbarui.',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (e) {
      console.error(e);
      Swal.fire({
        icon: 'error',
        title: 'Sinkronisasi Gagal',
        text: 'Koneksi bermasalah. Menggunakan database lokal.',
        timer: 2500,
        showConfirmButton: false,
      });
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    loadFromCache();
    syncFromAPI(false);
  }, []);

  useEffect(() => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      const all = await getAllAssets();
      const scoped = isAgen ? all.filter(a => isAssetForAgen(a, agenNama)) : all;
      if (isAgen) {
        setResults(filterAssetsByQuery(scoped, query));
      } else {
        if (!query.trim()) {
          setResults([]);
        } else {
          setResults(filterAssetsByQuery(scoped, query));
        }
      }
    }, 300);
    return () => clearTimeout(searchTimeout.current);
  }, [query, isAgen, agenNama, isAssetForAgen, filterAssetsByQuery]);

  const loadPengajuan = useCallback(async () => {
    setLoadingPengajuan(true);
    const list = await getAllPengajuan();
    setPengajuanList(list);
    setLoadingPengajuan(false);
    updateUnsyncedCount();
  }, [updateUnsyncedCount]);

  useEffect(() => {
    if (subTab === 'riwayat') loadPengajuan();
  }, [subTab, loadPengajuan]);

  const handleAjukanPenanganan = (asset) => {
    setShowDetail(false);
    setPendingAsset(asset);
    setShowFormPenanganan(true);
  };

  const handlePindahkanLokasi = (asset) => {
    setShowDetail(false);
    setPendingAsset(asset);
    setShowFormPindah(true);
  };

  const handleLocationUpdated = (lokasiBaru) => {
    if (pendingAsset) {
      setResults(prev => prev.map(item =>
        item.kode === pendingAsset.kode ? { ...item, lokasi: lokasiBaru } : item
      ));
    }
    setShowFormPindah(false);
    setPendingAsset(null);
  };

  const handleScanSuccess = async (scannedCode) => {
    if (!scannedCode) return;
    setIsScannerOpen(false);

    const all = await getAllAssets();
    const scoped = isAgen ? all.filter(a => isAssetForAgen(a, agenNama)) : all;
    const match = scoped.find(a => (a.kode || '').toLowerCase() === scannedCode.toLowerCase());
    if (match) {
      setSelectedAsset(match);
      setShowDetail(true);
      setQuery(match.kode);
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Asset Tidak Ditemukan',
        text: isAgen
          ? `Kode asset "${scannedCode}" tidak terdaftar di lokasi ${agenNama || 'agen Anda'}.`
          : `Kode asset: ${scannedCode}`,
        timer: 2500,
        showConfirmButton: false,
      });
      setQuery(scannedCode);
    }
  };

  const handleCekHistory = (asset) => {
    setShowDetail(false);
    setHistoryAsset(asset);
    setShowHistory(true);
  };

  const formatSync = (date) => {
    if (!date) return 'Belum pernah sync';
    const diff = Math.floor((Date.now() - date) / 60000);
    if (diff < 1) return 'Baru saja';
    if (diff < 60) return `${diff} mnt lalu`;
    return date.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
  };

  const SUB_TABS = [
    { id: 'cek', label: '🔍 Cek Asset' },
    { id: 'pengadaan', label: '📋 Pengadaan' },
    { id: 'riwayat', label: '📜 Riwayat' },
  ];

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 16 }}>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* Sub-tab */}
      <div style={{ display: 'flex', backgroundColor: '#fff', borderRadius: 16, boxShadow: '0 2px 10px rgba(0,0,0,0.04)', border: '1px solid #e5e7eb', overflow: 'hidden', width: '100%', maxWidth: 450, margin: '0 auto 12px auto', boxSizing: 'border-box' }}>
        {SUB_TABS.map((t, i) => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            style={{
              flex: 1, padding: '11px 8px', fontSize: 13, fontWeight: 600,
              color: subTab === t.id ? '#2563eb' : '#6b7280',
              backgroundColor: subTab === t.id ? '#eff6ff' : 'transparent',
              border: 'none',
              borderRight: i < SUB_TABS.length - 1 ? '1px solid #e5e7eb' : 'none',
              cursor: 'pointer', transition: 'all 0.2s ease', whiteSpace: 'nowrap',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Cek Asset */}
      {subTab === 'cek' && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} style={{ width: '100%', maxWidth: 450, margin: '0 auto', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: 24, padding: '20px', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)', border: '1px solid #cbd5e1', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: isAgen ? '#e0f2fe' : '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isAgen ? '#0284c7' : '#2563eb', flexShrink: 0 }}>
                  {isAgen ? <Store size={20} /> : <Building size={20} />}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>
                    {isAgen ? 'Lokasi Agen' : 'Kantor saya'}
                  </span>
                  <span style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>
                    {isAgen ? (agenNama || 'Agen') : (karyawan.kantor || 'Labib')}
                  </span>
                </div>
              </div>

              <button
                onClick={handleSyncAll}
                disabled={syncing}
                style={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: '#fff', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563eb', cursor: syncing ? 'not-allowed' : 'pointer', position: 'relative', transition: 'transform 0.15s' }}
                onMouseDown={e => e.currentTarget.style.transform = 'scale(0.92)'}
                onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
              >
                <RotateCw size={18} style={{ animation: syncing ? 'spin 1.5s linear infinite' : 'none' }} />
                {unsyncedCount > 0 && (
                  <span style={{ position: 'absolute', top: -3, right: -3, backgroundColor: '#ef4444', color: '#fff', borderRadius: '50%', minWidth: 16, height: 16, fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff', boxShadow: '0 2px 6px rgba(239,68,68,0.4)', padding: '0 2px' }}>
                    {unsyncedCount}
                  </span>
                )}
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, backgroundColor: '#e2e8f0', borderRadius: 20, padding: '10px 14px', border: '1px solid #cbd5e1', boxShadow: 'inset 2px 2px 5px rgba(0,0,0,0.13), inset -1px -1px 3px rgba(255,255,255,0.7)' }}>
                <Search size={18} color="#94a3b8" />
                <input
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder={isAgen ? (agenNama ? `Cari asset di ${agenNama}...` : "Cari asset agen...") : "Cari nama, kode atau lokasi asset..."}
                  style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13, backgroundColor: 'transparent', fontFamily: 'inherit', color: '#1e293b' }}
                  autoComplete="off"
                />
                {query && (
                  <button onClick={() => setQuery('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, fontSize: 14 }}>✕</button>
                )}
              </div>
              <button
                onClick={() => setIsScannerOpen(true)}
                style={{ width: 40, height: 40, backgroundColor: '#fff', color: '#2563eb', border: '1px solid #e2e8f0', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, transition: 'transform 0.15s' }}
                onMouseDown={e => e.currentTarget.style.transform = 'scale(0.92)'}
                onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
              >
                <QrCode size={18} />
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 4px', marginTop: -6 }}>
            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>
              {isAgen
                ? (query ? `${results.length} ditemukan dari ${allCount} asset agen` : `${allCount} asset di ${agenNama || 'agen'}`)
                : (query ? `${results.length} ditemukan` : `${allCount} asset tersimpan`)}
            </span>
            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>Sync: {formatSync(lastSync)}</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {results.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', backgroundColor: '#fff', borderRadius: 20, border: '1px dashed #e2e8f0', boxShadow: '0 4px 16px rgba(0,0,0,0.02)' }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>🔍</div>
                <p style={{ fontWeight: 700, color: '#4b5563', margin: 0, fontSize: 14 }}>
                  {isAgen
                    ? (!query ? 'Belum Ada Asset Terdaftar' : 'Asset Tidak Ditemukan')
                    : (!query ? 'Masukkan Kata Kunci' : 'Asset tidak ditemukan')}
                </p>
                <p style={{ color: '#9ca3af', fontSize: 12, margin: '4px 0 0 0' }}>
                  {isAgen
                    ? (!query ? `Tidak ada asset yang terdaftar di lokasi ${agenNama || 'agen Anda'}.` : `Coba kata kunci lain di ${agenNama || 'agen Anda'}.`)
                    : (!query ? 'Ketik kode, nama, atau lokasi asset untuk memulai pencarian.' : 'Coba kata kunci lain')}
                </p>
              </div>
            ) : (
              results.map(asset => {
                const { bg: kondisiBg, color: kondisiColor } = getKondisiStyle(asset.kondisi);
                return (
                  <motion.div
                    key={asset.kode}
                    initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                    onClick={() => { setSelectedAsset(asset); setShowDetail(true); }}
                    style={{ backgroundColor: '#fff', borderRadius: 18, padding: '16px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.03)', transition: 'all 0.2s ease' }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div style={{ width: 42, height: 42, borderRadius: 12, backgroundColor: '#f0f9ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
                      {getAssetIcon(asset.kategori)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{asset.nama}</div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 3, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                        <span style={{ fontFamily: 'monospace', backgroundColor: '#f1f5f9', padding: '2px 6px', borderRadius: 6, fontSize: 10, border: '1px solid #e2e8f0' }}>{asset.kode}</span>
                        <span style={{ color: '#cbd5e1' }}>·</span>
                        <span>{asset.lokasi || '-'}</span>
                      </div>
                      {(asset.serial_number || asset.keterangan) && (
                        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3, display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {asset.serial_number && (
                            <>
                              <span style={{ color: '#c084fc', fontWeight: 600 }}>S/N</span>
                              <span style={{ fontFamily: 'monospace' }}>{asset.serial_number}</span>
                            </>
                          )}
                          {asset.serial_number && asset.keterangan && <span style={{ color: '#e2e8f0' }}>·</span>}
                          {asset.keterangan && (
                            <span style={{ fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis' }}>📝 {asset.keterangan}</span>
                          )}
                        </div>
                      )}
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '4px 8px', borderRadius: 8, backgroundColor: kondisiBg, color: kondisiColor, flexShrink: 0, alignSelf: 'flex-start' }}>
                      {asset.kondisi || 'N/A'}
                    </span>
                  </motion.div>
                );
              })
            )}
          </div>
        </motion.div>
      )}

      {/* Pengadaan */}
      {subTab === 'pengadaan' && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} style={{ backgroundColor: '#fff', borderRadius: 20, padding: '24px 20px', width: '100%', maxWidth: 450, margin: '0 auto', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}>
          <div style={{ textAlign: 'center', marginBottom: 24, paddingBottom: 20, borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>📋</div>
            <h2 style={{ fontWeight: 700, fontSize: 18, margin: '0 0 4px 0', color: '#111827' }}>Pengajuan Pengadaan Aset</h2>
            <p style={{ margin: 0, color: '#9ca3af', fontSize: 13 }}>Ajukan kebutuhan aset baru untuk diproses</p>
          </div>
          <FormPengadaan onSuccess={() => { setSubTab('riwayat'); loadPengajuan(); }} />
        </motion.div>
      )}

      {/* Riwayat */}
      {subTab === 'riwayat' && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} style={{ width: '100%', maxWidth: 450, margin: '0 auto 20px auto', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: 20, padding: '20px', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)', border: '1px solid #cbd5e1', textAlign: 'center' }}>
            <h2 style={{ fontWeight: 700, fontSize: 18, margin: '0 0 4px 0', color: '#111827' }}>Riwayat Pengajuan</h2>
            <p style={{ margin: 0, color: '#9ca3af', fontSize: 13 }}>{pengajuanList.length} pengajuan tersimpan</p>
          </div>

          {loadingPengajuan ? (
            <div style={{ textAlign: 'center', padding: '32px', color: '#94a3b8' }}>Memuat riwayat...</div>
          ) : pengajuanList.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', backgroundColor: '#fff', borderRadius: 20, border: '1px dashed #e2e8f0' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📜</div>
              <p style={{ fontWeight: 600, color: '#4b5563', margin: 0 }}>Belum ada pengajuan</p>
              <p style={{ color: '#9ca3af', fontSize: 13, margin: '4px 0 0' }}>Pengajuan penanganan & pengadaan akan muncul di sini</p>
            </div>
          ) : (
            pengajuanList.map(p => <PengajuanCard key={p.id} p={p} />)
          )}
        </motion.div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {showDetail && selectedAsset && (
          <AssetDetailModal
            asset={selectedAsset}
            onClose={() => setShowDetail(false)}
            onAjukanPenanganan={handleAjukanPenanganan}
            onPindahkanLokasi={handlePindahkanLokasi}
            onCekHistory={handleCekHistory}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showHistory && historyAsset && (
          <AssetHistoryModal
            asset={historyAsset}
            isOpen={showHistory}
            onClose={() => { setShowHistory(false); setHistoryAsset(null); }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showFormPindah && pendingAsset && (
          <FormPindahLokasi
            asset={pendingAsset}
            onClose={() => { setShowFormPindah(false); setPendingAsset(null); }}
            onSuccess={handleLocationUpdated}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showFormPenanganan && pendingAsset && (
          <FormPenanganan
            asset={pendingAsset}
            onClose={() => { setShowFormPenanganan(false); setPendingAsset(null); }}
            onSuccess={() => {
              setShowFormPenanganan(false);
              setPendingAsset(null);
              setSubTab('riwayat');
              loadPengajuan();
            }}
          />
        )}
      </AnimatePresence>

      <AssetScannerModal
        isOpen={isScannerOpen}
        onClose={() => setIsScannerOpen(false)}
        onScanSuccess={handleScanSuccess}
      />
    </div>
  );
};

export default AssetTab;
